// Search Term Performance — the PRD's five components.
//
// Five views of one dataset, not five screens: a seller moving between the
// tabs is changing the question, not the subject, and a term carries the same
// figures wherever it appears.
//
// Separate from the Search Terms surface on purpose. That one grew to carry
// work from more than one document; this one answers to a single PRD so it can
// be checked against it line by line.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLink, FiLock } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './TermPerformance.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import FeatureCards from './components/FeatureCards';
import TermTable from './components/TermTable';
import BrandedSplit from './components/BrandedSplit';
import Cannibalization from './components/Cannibalization';
import { NotConnectedError, getBrandedSplit, getCannibalization, getOverview,
         getTerms, reclassify } from './api';
import { CLASS_LABEL, num } from './format';

// SF1, SF2 and SF4 are the same list asked three ways, so they share a
// component and differ only by `view`. SF3 and SF5 have their own shapes.
const TABS = [
  { id: 'top', label: 'Top search terms', ref: 'SF1', view: 'top' },
  { id: 'conversion', label: 'Conversion', ref: 'SF2', view: 'conversion' },
  { id: 'branded', label: 'Branded vs generic', ref: 'SF3' },
  { id: 'gaps', label: 'Gaps', ref: 'SF4', view: 'gaps' },
  { id: 'cannibalization', label: 'Cannibalization', ref: 'SF5' },
];

const SORTS = [
  ['clicks', 'Clicks'], ['impressions', 'Impressions'], ['volume', 'Search volume'],
  ['purchases', 'Purchases'], ['impression_share', 'Impression share'],
  ['click_share', 'Click share'], ['cart_add_share', 'Cart-add share'],
  ['purchase_share', 'Purchase share'], ['click_rate', 'Click rate'],
  ['conversion', 'Conversion'],
];

export default function TermPerformance() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [tab, setTab] = useState('top');
  const [period, setPeriod] = useState('WEEK');
  const [startDate, setStartDate] = useState('');
  const [windowOptions, setWindowOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [data, setData] = useState(null);
  const [split, setSplit] = useState(null);
  const [cannibal, setCannibal] = useState(null);
  const [includeVariations, setIncludeVariations] = useState(false);

  const [classification, setClassification] = useState('');
  const [sort, setSort] = useState('clicks');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  const active = TABS.find((t) => t.id === tab);

  // The cards describe the window, so they follow the window and nothing else.
  useEffect(() => {
    if (!token || !connected) return undefined;
    let live = true;
    getOverview(token, { period, start_date: startDate })
      .then((r) => {
        if (!live) return;
        setOverview(r);
        setWindowOptions(r.window_options || []);
        setLocked(r.locked ? r : null);
        if (r.start_date && !startDate) setStartDate(r.start_date);
      })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, connected, period, startDate, fail]);

  const load = useCallback(async () => {
    if (!token || !connected) return;
    setLoading(true);
    setError(null);
    try {
      const args = { period, start_date: startDate };
      if (tab === 'branded') setSplit(await getBrandedSplit(token, args));
      else if (tab === 'cannibalization') {
        setCannibal(await getCannibalization(token, {
          ...args, include_variations: includeVariations || undefined }));
      } else {
        setData(await getTerms(token, {
          ...args, view: active.view, classification, sort, search: search.trim(),
          page, page_size: 50 }));
      }
    } catch (e) { fail(e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, connected, tab, period, startDate, classification, sort, page,
      includeVariations]);

  useEffect(() => { load(); }, [load]);

  const correct = async (term, label) => {
    try {
      await reclassify(token, term, label);
      load();
      if (tab !== 'branded') getBrandedSplit(token, { period, start_date: startDate })
        .then(setSplit).catch(() => {});
    } catch (e) { fail(e); }
  };

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>Search term data comes from your Amazon account.</p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={period}
                        onChange={(e) => { setPeriod(e.target.value); setStartDate(''); setPage(1); }}>
                  {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={s.field}>
                <span className={s.label}>Window</span>
                <select className={s.select} value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }}>
                  {!windowOptions.length && <option value="">No data yet</option>}
                  {windowOptions.map((w) => (
                    <option key={w.start_date} value={w.start_date}>
                      {w.start_date}{w.collected ? '' : ' — not collected'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {overview?.coverage && (
              <div className={overview.coverage.complete ? s.coverage : s.coverageWarn}>
                {num(overview.coverage.products_with_data)} of{' '}
                {num(overview.coverage.products_in_catalogue)} products have search term
                data for this window
                {overview.coverage.products_at_term_cap > 0 && (
                  <> · {num(overview.coverage.products_at_term_cap)} returned the maximum{' '}
                    {num(overview.coverage.terms_per_product_cap)} terms, so their tail is
                    cut and these totals are a floor</>
                )}
              </div>
            )}
          </div>
        </section>

        {!locked && (
          <FeatureCards data={overview} loading={loading && !overview}
                        onOpen={(next) => { setTab(next); setPage(1); }} />
        )}

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => { setTab(t.id); setPage(1); }}>
                {t.label}<span className={s.tabRef}>{t.ref}</span>
              </button>
            ))}
          </div>

          {locked ? (
            <div className={s.locked}>
              <div className={s.lockedIcon}><FiLock /></div>
              <h2 className={s.lockedTitle}>Search term data is not available yet</h2>
              <p className={s.lockedBody}>
                Amazon serves this report to the owner of a brand in Brand Registry. An
                authorised seller of someone else&apos;s brand is refused even when the
                account appears in Brand Registry.
              </p>
            </div>
          ) : (
            <div style={{ paddingTop: 16 }}>
              {(tab === 'top' || tab === 'conversion' || tab === 'gaps') && (
                <>
                  <div className={s.controls} style={{ marginBottom: 14 }}>
                    <label className={s.field}>
                      <span className={s.label}>Class</span>
                      <select className={s.select} value={classification}
                              onChange={(e) => { setClassification(e.target.value); setPage(1); }}>
                        <option value="">All terms</option>
                        {Object.entries(CLASS_LABEL).map(([k, v]) =>
                          <option key={k} value={k}>{v}</option>)}
                      </select>
                    </label>
                    {tab === 'top' && (
                      <label className={s.field}>
                        <span className={s.label}>Sort by</span>
                        <select className={s.select} value={sort}
                                onChange={(e) => { setSort(e.target.value); setPage(1); }}>
                          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </label>
                    )}
                    <label className={s.field}>
                      <span className={s.label}>Search</span>
                      <input className={s.input} value={search} placeholder="Filter terms…"
                             onChange={(e) => setSearch(e.target.value)}
                             onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }} />
                    </label>
                    <button className={s.secondary} onClick={() => { setPage(1); load(); }}>
                      Apply
                    </button>
                  </div>
                  {tab === 'conversion' && (
                    <p className={s.hint} style={{ marginBottom: 12 }}>
                      Ranked by click to purchase — the rate the seller can act on, since
                      impressions are Amazon&apos;s decision and clicks are the
                      shopper&apos;s. Terms with too few clicks keep their figures but are
                      held out of the ranking.
                    </p>
                  )}
                  {tab === 'gaps' && (
                    <p className={s.hint} style={{ marginBottom: 12 }}>
                      Terms you appear on but barely capture, ranked by impressions missed.
                      Terms you never appear for produce no row at all, so they cannot be
                      shown here.
                    </p>
                  )}
                  {loading ? <div className={s.empty}>Loading…</div>
                    : !data?.terms?.length ? (
                      <div className={s.empty}>No search terms for this window.</div>
                    ) : (
                      <>
                        <TermTable view={active.view} terms={data.terms} sort={data.sort}
                                   onCorrect={correct} />
                        <div className={s.pagination}>
                          <span>
                            {num(data.pagination.total)} terms · page {data.pagination.page}
                            {' '}of {data.pagination.total_pages}
                          </span>
                          <button className={s.pageButton} disabled={page <= 1}
                                  onClick={() => setPage(page - 1)}>Previous</button>
                          <button className={s.pageButton}
                                  disabled={page >= data.pagination.total_pages}
                                  onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                      </>
                    )}
                </>
              )}

              {tab === 'branded' && (
                <BrandedSplit data={split} onFilter={(label) => {
                  setClassification(label); setTab('top'); setPage(1);
                }} />
              )}

              {tab === 'cannibalization' && (
                <Cannibalization data={cannibal} includeVariations={includeVariations}
                                 onToggleVariations={setIncludeVariations} />
              )}
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Search Term Performance</h1></div>
          <p>Which terms bring traffic, which bring buyers, and where you are losing them</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
